import { useCallback, useEffect, useRef, useState } from 'react';
import { AppHeader } from '../components/AppHeader';
import { PumpCard } from '../components/PumpCard';
import { ApiError, getFn } from '../lib/apiClient';
import { openPumpStateStream } from '../lib/sseClient';
import type { Device, DeviceListResponse } from '../lib/types';

/**
 * Fleet view — `/dashboard`. Calls `deviceList` for the initial load,
 * then subscribes to the `pumpState` SSE stream so cards update in
 * real time as pump telemetry arrives:
 *
 *   - `state` → re-fetch deviceList (cheap, small payload, keeps the
 *     wire format authoritative on the server).
 *   - `pump_online` / `pump_first_connected` → optimistically flip
 *     that card's `connectionStatus.isConnected = true`.
 *   - `pump_offline` → optimistically flip to `false`.
 *
 * The 30s background refetch from Phase 4 stays as a backstop for
 * environments where SSE is blocked (corporate proxies, captive
 * portals). When SSE is healthy it'll typically beat the timer.
 */
export function Dashboard() {
  const [devices, setDevices] = useState<Device[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sseConnected, setSseConnected] = useState(false);

  // Stable ref to the current device list so the SSE handler can
  // mutate it without re-creating the subscription on every render.
  const devicesRef = useRef<Device[] | null>(null);
  devicesRef.current = devices;

  const reload = useCallback(async () => {
    setError(null);
    try {
      const res = await getFn<DeviceListResponse>('deviceList');
      setDevices(res.Items || []);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Unable to load your pumps.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch + 30s background refresh as a backstop.
  useEffect(() => {
    void reload();
    const id = window.setInterval(() => void reload(), 30_000);
    return () => window.clearInterval(id);
  }, [reload]);

  // SSE subscription — opens once on mount, closes on unmount.
  useEffect(() => {
    const conn = openPumpStateStream({
      onOpen: () => setSseConnected(true),
      onError: () => setSseConnected(false),
      onEvent: (event) => {
        const payload = event.payload as
          | { deviceId?: string; status?: 'online' | 'offline' }
          | null;
        const deviceId = payload?.deviceId;
        if (!deviceId) return;

        if (event.type === 'state') {
          // Authoritative re-sync: the translator just persisted new
          // telemetry. Cheapest correct thing to do is refetch.
          void reload();
          return;
        }

        if (
          event.type === 'pump_online' ||
          event.type === 'pump_first_connected' ||
          event.type === 'pump_offline'
        ) {
          const online =
            event.type === 'pump_online' ||
            event.type === 'pump_first_connected';
          // Optimistic local update for snappy UX.
          setDevices((prev) => {
            if (!prev) return prev;
            return prev.map((d) =>
              d.deviceId === deviceId
                ? {
                    ...d,
                    connectionStatus: {
                      ...d.connectionStatus,
                      isConnected: online,
                    },
                  }
                : d,
            );
          });
        }
      },
    });
    return () => conn.close();
  }, [reload]);

  return (
    <>
      <AppHeader />
      <main className="container py-4">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div>
            <h2 className="h3 mb-0" style={{ color: 'var(--aqua-text-strong)' }}>
              My pumps
            </h2>
            <small className="text-muted">
              {sseConnected ? (
                <span style={{ color: 'var(--aqua-online)' }}>● Live</span>
              ) : (
                <span className="text-muted">○ Reconnecting…</span>
              )}
            </small>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => void reload()}
            disabled={loading}
            title="Refresh"
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        {loading && devices === null && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading…</span>
            </div>
          </div>
        )}

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {devices !== null && devices.length === 0 && !error && (
          <div className="card border-0" style={{ background: 'transparent' }}>
            <div className="card-body text-center py-5">
              <h3 className="h5 mb-2" style={{ color: 'var(--aqua-text-strong)' }}>
                You don't have any pumps yet
              </h3>
              <p className="text-muted mb-0">
                Open the AquaStar mobile app to add your first pump, or ask the
                person who installed your system to share access.
              </p>
            </div>
          </div>
        )}

        {devices !== null && devices.length > 0 && (
          <div
            className="d-flex flex-column gap-3"
            style={{ maxWidth: 720, margin: '0 auto' }}
          >
            {devices.map((d) => (
              <PumpCard key={d.deviceId} device={d} />
            ))}
          </div>
        )}
      </main>
    </>
  );
}
